import { useEffect, useState } from "react";
import { Disc3, Radio, Clock, FileEdit, Play, Loader2, Lock, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatCard, StatusPill, EmptyState } from "@/components/dashboard/ui";
import UpgradeBanner from "@/components/billing/UpgradeBanner";
import HomeSearch from "@/components/dashboard/HomeSearch";
import ReleaseCalendar from "@/components/dashboard/ReleaseCalendar";
import ActivityFeed from "@/components/dashboard/ActivityFeed";

export default function ArtistOverview({ goto }) {
  const { user } = useAuth();
  const distActive = !!user?.distribution_active;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/home");
        setData(res.data);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;
  }

  const s = data?.stats || {};
  const recent = data?.recent || [];
  const upcoming = data?.upcoming || [];
  const activity = data?.activity || [];

  return (
    <div className="space-y-8" data-testid="artist-overview">
      <UpgradeBanner recommendedTier="artist" goto={goto} />

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">Home</div>
          <h2 className="mt-1 font-display text-3xl md:text-4xl tracking-tight text-white">
            Welcome back, {user?.display_name || user?.name}.
          </h2>
          <p className="mt-1.5 text-slate-400">Search your catalog, track upcoming releases and watch new activity flow in.</p>
        </div>
        <button
          data-testid="overview-new-release"
          onClick={() => goto("releases")}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 transition-colors self-start md:self-auto"
        >
          New release <ArrowUpRight size={16} />
        </button>
      </div>

      <HomeSearch goto={goto} />

      {!distActive && (
        <div data-testid="overview-dist-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-4 flex items-start gap-3">
          <Lock size={18} className="text-amber-300 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-200/90">
            Distribution isn&apos;t active yet. Your admin will switch it on once your subscription is confirmed — then you can create releases and deliver to stores.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total releases" value={s.total_releases ?? 0} icon={Disc3} />
        <StatCard label="Live" value={s.live ?? 0} icon={Radio} />
        <StatCard label="In review" value={s.in_review ?? 0} icon={Clock} />
        <StatCard label="Drafts" value={s.drafts ?? 0} icon={FileEdit} />
        <div className="col-span-2 lg:col-span-1 rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-600/15 to-transparent px-5 py-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase font-bold text-blue-300">
            <Play size={12} /> Total streams
          </div>
          <div className="mt-1.5 font-display text-2xl text-white">{(s.total_streams ?? 0).toLocaleString()}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3">
          <ReleaseCalendar upcoming={upcoming} goto={goto} />
        </div>
        <div className="lg:col-span-2">
          <ActivityFeed events={activity} goto={goto} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl text-white">Recent releases</h3>
          {recent.length > 0 && (
            <button
              data-testid="overview-view-releases"
              onClick={() => goto("releases")}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              View all
            </button>
          )}
        </div>
        {recent.length === 0 ? (
          <EmptyState title="No releases yet" desc="Create your first release from the My Releases tab." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {recent.map((r) => (
              <button
                key={r.release_id}
                onClick={() => goto("releases")}
                className="text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] px-4 py-3.5 flex items-center gap-4 transition-colors"
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/20 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <Disc3 size={18} className="text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500">{r.type}</div>
                </div>
                <StatusPill status={r.status} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
